import { create } from "zustand";
import type { MapObject } from "../models/mapObject";

/**
 * Core map VIEW / interaction state, extracted from MapCanvas.tsx (Phase 3 of the
 * MapCanvas clean-architecture redesign — see plan/map-architecture/).
 *
 * Scope: the plain, serializable view/interaction flags that gate the PIXI render
 * pipeline and the map effects. These were previously `useState` in MapCanvas and
 * (for the ready-flags) threaded as setter params into the map hooks
 * (useMapScene / useMapObjectsRender). The hooks still receive plain setters/values
 * as params — MapCanvas is the only store-binding site — so those hooks stay
 * store-agnostic and no back-import exists.
 *
 * Field/action names match the removed MapCanvas locals 1:1 so every downstream
 * read/write/effect-dep is unchanged: only the `useState` declaration is swapped
 * for a selector, and setters passed to hooks are swapped for store actions.
 */
interface MapViewStore {
    /** Currently selected/highlighted map object (plot/house/cityhall/etc.), or null. */
    selectedMapObject: MapObject | null;
    setSelectedMapObject: (value: MapObject | null) => void;
    /** Admin "god mode" toggle (only effective when userData.is_admin). */
    isAdminMode: boolean;
    setIsAdminMode: (value: boolean) => void;
    /** Whether plot/sale/quest/avatar markers are visible (top-bar toggle). */
    showPlots: boolean;
    setShowPlots: (value: boolean) => void;
    /** True once the object-marker render pass has populated the layers. Gates many effects. */
    markersReady: boolean;
    setMarkersReady: (value: boolean) => void;
    /**
     * True once the whole map is drawn — islands, houses, every object. The
     * splash stays up until then (ServerConnectGate), so the map appears whole.
     */
    mapSceneBuilt: boolean;
    setMapSceneBuilt: (value: boolean) => void;
    /** True once the PIXI app/scene has finished initializing. Gates every render effect. */
    isPixiAppReady: boolean;
    setIsPixiAppReady: (value: boolean) => void;
    /** Camera intent: onboarding requested a forced zoom to the user's plot. */
    shouldZoomToPlot: boolean;
    setShouldZoomToPlot: (value: boolean) => void;
}

const useMapViewStore = create<MapViewStore>((set) => ({
    selectedMapObject: null,
    setSelectedMapObject: (value) => set({ selectedMapObject: value }),
    isAdminMode: false,
    setIsAdminMode: (value) => set({ isAdminMode: value }),
    showPlots: true,
    setShowPlots: (value) => set({ showPlots: value }),
    markersReady: false,
    setMarkersReady: (value) => set({ markersReady: value }),
    mapSceneBuilt: false,
    setMapSceneBuilt: (value) => set({ mapSceneBuilt: value }),
    isPixiAppReady: false,
    setIsPixiAppReady: (value) => set({ isPixiAppReady: value }),
    shouldZoomToPlot: false,
    setShouldZoomToPlot: (value) => set({ shouldZoomToPlot: value }),
}));

export default useMapViewStore;
